import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, MessageSquare, Paperclip, FileText, Settings as SettingsIcon, Scroll,
  Plus, Edit3, Trash2, X, Check, Save, Eye, Sparkles, Search, Upload,
  Download, Copy as CopyIcon, FileType, Palette, AlertCircle, Files,
  CalendarDays, CalendarOff,
} from "lucide-react";
import TiptapEditor from "./TiptapEditor";
import ShiftTemplatesAdmin from "./ShiftTemplatesAdmin";
import AbsenceTemplatesAdmin from "./AbsenceTemplatesAdmin";
import DOMPurify from "dompurify";

const SUBTABS = [
  { key: "shift-plans", label: "Schichtplan",            icon: CalendarDays },
  { key: "absences",    label: "Abwesenheit",            icon: CalendarOff },
  { key: "templates",   label: "E-Mail & SMS",         icon: Mail },
  { key: "attachments", label: "Anhänge",              icon: Paperclip },
  { key: "doc-presets", label: "Anhang-Voreinstellungen", icon: Files },
  { key: "pdf",         label: "PDF-Vorlagen",         icon: FileText },
  { key: "mappings",    label: "Zuordnungen",          icon: SettingsIcon },
  { key: "settings",    label: "Globale Einstellungen", icon: SettingsIcon },
  { key: "contracts",   label: "Verträge",             icon: Scroll },
];

const inputCls = "w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-blue-500";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VorlagenHub() {
  const [tab, setTab] = useState("shift-plans");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/vorlagen/_meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  if (!meta) return <div className="text-zinc-500 text-sm p-4">Lade Vorlagen…</div>;

  return (
    <div className="space-y-5" data-testid="vorlagen-hub">
      {/* ===== Sub-Tab Navigation ===== */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-1 inline-flex flex-wrap gap-1">
        {SUBTABS.map((s) => {
          const I = s.icon;
          const active = tab === s.key;
          return (
            <button key={s.key} onClick={() => setTab(s.key)}
              data-testid={`vorlagen-subtab-${s.key}`}
              className={`flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded transition-colors ${active ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"}`}>
              <I className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {tab === "shift-plans" && <ShiftTemplatesAdmin />}
      {tab === "absences"    && <AbsenceTemplatesAdmin />}
      {tab === "templates"   && <EmailSmsTemplates meta={meta} />}
      {tab === "attachments" && <AttachmentsManager />}
      {tab === "doc-presets" && <DocPresetsManager meta={meta} />}
      {tab === "pdf"         && <PdfLayoutsManager meta={meta} />}
      {tab === "mappings"    && <EventMappingsTab />}
      {tab === "settings"    && <GlobalSettingsTab />}
      {tab === "contracts"   && <ContractsManager meta={meta} />}
    </div>
  );
}

// ===========================================================================
// 1) E-Mail & SMS Templates
// ===========================================================================
function EmailSmsTemplates({ meta }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  async function load() {
    try {
      const url = filter ? `/vorlagen/templates?category=${filter}` : "/vorlagen/templates";
      const { data } = await api.get(url);
      setItems(data || []);
    } catch { toast.error("Konnte Vorlagen nicht laden"); }
  }
  useEffect(() => { load(); }, [filter]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter((it) => (it.name || "").toLowerCase().includes(s)
                                || (it.subject || "").toLowerCase().includes(s)
                                || (it.event_trigger || "").toLowerCase().includes(s));
  }, [items, search]);

  async function remove(it) {
    if (!window.confirm(`Vorlage „${it.name}" löschen?`)) return;
    try { await api.delete(`/vorlagen/templates/${it.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-400" /> E-Mail- &amp; SMS-Vorlagen
          </h4>
          <p className="text-xs text-zinc-500 mt-0.5">Vorlagen mit Variablen-Platzhaltern für automatischen + manuellen Versand.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded">
            {[{ k: "", l: "Alle" }, { k: "email", l: "E-Mail" }, { k: "sms", l: "SMS" }].map((c) => (
              <button key={c.k} onClick={() => setFilter(c.k)}
                className={`px-3 py-1 text-xs ${filter === c.k ? "bg-blue-600 text-white rounded" : "text-zinc-400 hover:text-zinc-100"}`}>
                {c.l}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen…" className="bg-zinc-950 border border-zinc-800 rounded pl-7 pr-3 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 w-40" />
          </div>
          <button onClick={() => setEditing({ __new: true, category: "email", event_trigger: "manual", is_active: true, name: "", subject: "", body: "", default_cc: [], default_bcc: [], attachment_ids: [] })}
            data-testid="tpl-add-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
            <Plus className="w-3.5 h-3.5" /> Neue Vorlage
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Empty icon={Mail} text='Noch keine Vorlagen vorhanden. Klicken Sie auf „Neue Vorlage".' />
      ) : (
        <div className="divide-y divide-zinc-800">
          {filtered.map((it) => {
            const ev = (meta.events || []).find((e) => e.key === it.event_trigger);
            return (
              <div key={it.id} data-testid={`tpl-row-${it.id}`}
                className="px-5 py-3 hover:bg-zinc-900/40 flex items-center gap-3">
                <div className="flex-shrink-0">
                  {it.category === "sms"
                    ? <MessageSquare className="w-4 h-4 text-emerald-400" />
                    : <Mail className="w-4 h-4 text-blue-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-100 font-medium text-sm">{it.name}</span>
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                      {it.category === "sms" ? "SMS" : "E-MAIL"}
                    </span>
                    {ev && ev.key !== "manual" && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">
                        {ev.label}
                      </span>
                    )}
                    {!it.is_active && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500 border border-zinc-700">
                        Inaktiv
                      </span>
                    )}
                  </div>
                  {it.subject && <div className="text-xs text-zinc-400 mt-0.5 truncate">{it.subject}</div>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditing({ ...it })} data-testid={`tpl-edit-${it.id}`}
                    className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(it)} data-testid={`tpl-delete-${it.id}`}
                    className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <TemplateEditModal initial={editing} meta={meta} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateEditModal({ initial, meta, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({ ...initial });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [varSearch, setVarSearch] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef(null);
  const subjectRef = useRef(null);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  useEffect(() => {
    api.get("/vorlagen/attachments").then(({ data }) => setAttachments(data || [])).catch(() => {});
  }, []);

  function insertVar(varKey) {
    // If subject input is currently focused, insert there. Otherwise into body.
    const ae = document.activeElement;
    if (ae && ae === subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;
      const cur = form.subject || "";
      up("subject", cur.slice(0, start) + varKey + cur.slice(end));
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + varKey.length, start + varKey.length);
      }, 10);
      return;
    }
    if (form.category === "email" && editorRef.current) {
      editorRef.current.insertText(varKey);
      return;
    }
    // SMS body fallback (textarea)
    const ta = document.querySelector('[data-testid="tpl-modal-body-sms"]');
    if (ta) {
      const start = ta.selectionStart || 0;
      const end = ta.selectionEnd || 0;
      const cur = form.body || "";
      up("body", cur.slice(0, start) + varKey + cur.slice(end));
      setTimeout(() => { ta.focus(); ta.setSelectionRange(start + varKey.length, start + varKey.length); }, 10);
    } else {
      up("body", (form.body || "") + varKey);
    }
  }

  async function doPreview() {
    if (isNew && !form.id) {
      const sampleCtx = {
        "#MyCompanyName#": "Munja Trans GmbH", "#Today#": new Date().toISOString().slice(0, 10),
        "#EmployeeName#": "Max Mustermann", "#InvoiceNumber#": "RE-2026-0042",
        "#CustomerName#": "Beispiel Kunde GmbH", "#VehiclePlate#": "W-12345A",
        "#InvoiceGross#": "1.500,00 €",
      };
      let s = form.subject || "", b = form.body || "";
      for (const [k, v] of Object.entries(sampleCtx)) { s = s.replaceAll(k, v); b = b.replaceAll(k, v); }
      setPreview({ subject: s, body: b });
      setShowPreview(true);
      return;
    }
    try {
      const { data } = await api.post(`/vorlagen/templates/${initial.id}/preview`);
      setPreview(data);
      setShowPreview(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
  }

  async function submit() {
    if (!form.name?.trim()) { toast.error("Name ist erforderlich"); return; }
    if (form.category === "email" && !form.subject?.trim()) { toast.error("Betreff ist bei E-Mail erforderlich"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.__new; delete payload.id; delete payload.tenant_id;
      delete payload.created_at; delete payload.updated_at;
      if (isNew) await api.post("/vorlagen/templates", payload);
      else await api.put(`/vorlagen/templates/${initial.id}`, payload);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  }

  const filteredVars = (meta.placeholders || []).filter((p) =>
    !varSearch || p.label.toLowerCase().includes(varSearch.toLowerCase())
                || p.key.toLowerCase().includes(varSearch.toLowerCase())
                || p.category.toLowerCase().includes(varSearch.toLowerCase())
  );
  const groupedVars = filteredVars.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-7xl my-4 flex flex-col overflow-hidden max-h-[92vh]" onClick={(e) => e.stopPropagation()} data-testid="tpl-edit-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0 bg-zinc-900">
          <h3 className="font-display font-semibold text-zinc-100">{isNew ? "Neue Vorlage" : `Vorlage bearbeiten: ${initial.name}`}</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowPreview((s) => !s); if (!showPreview) doPreview(); }} type="button" data-testid="tpl-modal-preview-toggle"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">
              <Eye className="w-3.5 h-3.5" /> {showPreview ? "Vorschau ausblenden" : "Vorschau"}
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] flex-1 min-h-0 overflow-hidden">
          {/* ===== Editor column ===== */}
          <div className="p-5 space-y-3 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Typ</Label>
                <select value={form.category} onChange={(e) => up("category", e.target.value)} className={inputCls}>
                  <option value="email">E-Mail</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              <div>
                <Label>Auslöser</Label>
                <select value={form.event_trigger} onChange={(e) => up("event_trigger", e.target.value)} className={inputCls}>
                  {(meta.events || []).map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label required>Name (intern)</Label>
              <input value={form.name || ""} onChange={(e) => up("name", e.target.value)}
                data-testid="tpl-modal-name" className={inputCls}
                placeholder="z.B. Mahnung 1 freundlich" />
            </div>
            {form.category === "email" && (
              <div>
                <Label required>Betreff</Label>
                <input ref={subjectRef} value={form.subject || ""} onChange={(e) => up("subject", e.target.value)}
                  data-testid="tpl-modal-subject" className={inputCls + " font-mono text-xs"}
                  placeholder="Rechnung #InvoiceNumber# fällig am #InvoiceDueDate#" />
              </div>
            )}
            <div>
              <Label required>Inhalt</Label>
              {form.category === "email" ? (
                <TiptapEditor
                  ref={editorRef}
                  value={form.body || ""}
                  onChange={(html) => up("body", html)}
                  placeholder="Sehr geehrte/r #CustomerContact#, anbei finden Sie unsere Rechnung #InvoiceNumber#…"
                />
              ) : (
                <>
                  <textarea value={form.body || ""} onChange={(e) => up("body", e.target.value)}
                    rows={5}
                    data-testid="tpl-modal-body-sms"
                    className={inputCls + " font-mono text-xs"}
                    placeholder="Hi #EmployeeFirstName#, …" />
                  <p className="text-[10px] text-zinc-600 mt-1">SMS-Länge: {(form.body || "").length} / 160 Zeichen</p>
                </>
              )}
            </div>

            {form.category === "email" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Standard-CC (kommagetrennt)</Label>
                    <input value={(form.default_cc || []).join(", ")}
                      onChange={(e) => up("default_cc", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                      placeholder="cc@example.at" className={inputCls + " text-xs"} />
                  </div>
                  <div>
                    <Label>Standard-BCC</Label>
                    <input value={(form.default_bcc || []).join(", ")}
                      onChange={(e) => up("default_bcc", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                      placeholder="bcc@example.at" className={inputCls + " text-xs"} />
                  </div>
                </div>
                {attachments.length > 0 && (
                  <div>
                    <Label>Standard-Anhänge</Label>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {attachments.map((a) => {
                        const checked = (form.attachment_ids || []).includes(a.id);
                        return (
                          <label key={a.id} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs cursor-pointer ${checked ? "bg-blue-500/10 border-blue-500/50 text-blue-200" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}>
                            <input type="checkbox" checked={checked}
                              onChange={(e) => {
                                const set = new Set(form.attachment_ids || []);
                                if (e.target.checked) set.add(a.id); else set.delete(a.id);
                                up("attachment_ids", Array.from(set));
                              }}
                              className="w-3 h-3 accent-blue-600" />
                            <Paperclip className="w-3 h-3" /> {a.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => up("is_active", e.target.checked)}
                className="w-4 h-4 rounded accent-blue-600" />
              <span className="text-sm text-zinc-200">Aktiv</span>
            </label>

            {showPreview && preview && (
              <div className="mt-4 border border-zinc-800 rounded-md bg-zinc-950 overflow-hidden" data-testid="tpl-preview-block">
                <div className="px-3 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 flex items-center justify-between">
                  <span>Live-Vorschau (Beispieldaten)</span>
                  <button onClick={doPreview} type="button" className="text-zinc-400 hover:text-zinc-200"><Eye className="w-3 h-3" /></button>
                </div>
                {form.category === "email" && preview.subject && (
                  <div className="px-3 py-2 border-b border-zinc-800/50">
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Betreff</div>
                    <div className="text-sm text-zinc-100 mt-0.5">{preview.subject}</div>
                  </div>
                )}
                <div className="px-3 py-3 min-h-[160px]">
                  {form.category === "sms" ? (
                    <pre className="text-sm text-zinc-200 whitespace-pre-wrap font-sans">{preview.body}</pre>
                  ) : (
                    <div className="text-sm text-zinc-200 prose prose-invert prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.body || "") }} />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ===== Permanent Variable Sidebar (right) ===== */}
          <div className="border-l border-zinc-800 bg-zinc-950 p-3 overflow-y-auto" data-testid="tpl-var-sidebar">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-blue-400" /> Variablen
              </div>
            </div>
            <div className="relative mb-2">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={varSearch} onChange={(e) => setVarSearch(e.target.value)}
                placeholder="Suchen…"
                data-testid="tpl-var-search"
                className="w-full bg-zinc-900 border border-zinc-800 rounded pl-6 pr-2 py-1 text-[11px] text-zinc-100 focus:outline-none focus:border-blue-500" />
            </div>
            <p className="text-[9px] text-zinc-600 mb-2">Klicken fügt am Cursor ein.</p>
            <div className="space-y-2.5">
              {Object.entries(groupedVars).map(([cat, vars]) => (
                <div key={cat}>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{cat}</div>
                  <div className="flex flex-col gap-0.5">
                    {vars.map((v) => (
                      <button key={v.key} onClick={() => insertVar(v.key)}
                        type="button"
                        data-testid={`tpl-var-${v.key.replaceAll('#', '')}`}
                        className="text-left text-[10px] px-2 py-1 bg-zinc-900 hover:bg-blue-600 hover:text-white text-zinc-300 rounded font-mono transition-colors flex items-center justify-between gap-2 group"
                        title={v.label}>
                        <span className="truncate">{v.key}</span>
                        <span className="text-[9px] text-zinc-500 group-hover:text-blue-100 truncate hidden lg:inline">{v.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {Object.keys(groupedVars).length === 0 && (
                <p className="text-[10px] text-zinc-600 text-center py-4">Keine Variablen gefunden.</p>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 flex-shrink-0 bg-zinc-900">
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid="tpl-modal-save"
            className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded">
            <Save className="w-3.5 h-3.5" /> {saving ? "Speichere…" : isNew ? "Anlegen" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// 2) Reusable Attachments
// ===========================================================================
function AttachmentsManager() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  async function load() {
    try { const { data } = await api.get("/vorlagen/attachments"); setItems(data || []); }
    catch { toast.error("Konnte Anhänge nicht laden"); }
  }
  useEffect(() => { load(); }, []);

  async function upload() {
    if (!file) { toast.error("Bitte Datei wählen"); return; }
    if (!name.trim()) { toast.error("Bitte Name angeben"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/vorlagen/attachments?name=${encodeURIComponent(name)}&description=${encodeURIComponent(desc)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Anhang hochgeladen");
      setName(""); setDesc(""); setFile(null);
      document.getElementById("att-file-input").value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  }

  async function remove(it) {
    if (!window.confirm(`„${it.name}" löschen? Wird aus allen Vorlagen entfernt, die ihn benutzen.`)) return;
    try { await api.delete(`/vorlagen/attachments/${it.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-emerald-400" /> Voreingestellte Dokumentenanhänge
          </h4>
          <p className="text-xs text-zinc-500 mt-0.5">PDFs (z.B. AGB, Datenschutz, Lieferbedingungen), die in beliebigen E-Mail-Vorlagen automatisch beigefügt werden.</p>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Name (z.B. AGB 2026)" className={inputCls} />
          <input value={desc} onChange={(e) => setDesc(e.target.value)}
            placeholder="Beschreibung (optional)" className={inputCls} />
          <div className="flex gap-2">
            <input id="att-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="att-file-input"
              className="flex-1 text-xs text-zinc-400 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-zinc-800 file:text-zinc-200 file:cursor-pointer" />
            <button onClick={upload} disabled={uploading}
              data-testid="att-upload-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded font-medium">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Lade…" : "Hochladen"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        {items.length === 0 ? (
          <Empty icon={Paperclip} text="Noch keine Anhänge hochgeladen." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-5 py-3 text-left font-semibold">Name</th>
                <th className="px-3 py-3 text-left font-semibold">Datei</th>
                <th className="px-3 py-3 text-left font-semibold">Beschreibung</th>
                <th className="px-3 py-3 text-right font-semibold w-24">Größe</th>
                <th className="px-3 py-3 text-right font-semibold w-32"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-zinc-800/60" data-testid={`att-row-${it.id}`}>
                  <td className="px-5 py-3 text-zinc-100 font-medium">{it.name}</td>
                  <td className="px-3 py-3 text-zinc-400 text-xs font-mono">{it.filename}</td>
                  <td className="px-3 py-3 text-zinc-500 text-xs">{it.description || "—"}</td>
                  <td className="px-3 py-3 text-right text-zinc-400 text-xs font-mono">{((it.size_bytes || 0) / 1024).toFixed(1)} KB</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <a href={`${BACKEND_URL}/api/vorlagen/attachments/${it.id}/download`} target="_blank" rel="noopener noreferrer"
                        className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded inline-flex">
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button onClick={() => remove(it)} data-testid={`att-delete-${it.id}`}
                        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// 3) PDF Templates (Phase 5K — Kodlogy-style template editor)
// ===========================================================================
function PdfLayoutsManager({ meta }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  async function load() {
    try { const { data } = await api.get("/vorlagen/pdf-templates"); setItems(data || []); }
    catch { toast.error("Konnte PDF-Vorlagen nicht laden"); }
  }
  useEffect(() => { load(); }, []);

  async function remove(it) {
    if (!window.confirm(`Vorlage „${it.name}" löschen?`)) return;
    try { await api.delete(`/vorlagen/pdf-templates/${it.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  async function duplicate(it) {
    try {
      const payload = { ...it };
      delete payload.id; delete payload.tenant_id; delete payload.label;
      delete payload.created_at; delete payload.updated_at;
      payload.name = `${it.name} (Kopie)`;
      const { data } = await api.post("/vorlagen/pdf-templates", payload);
      setEditing(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-400" /> PDF-Vorlagen
            </h4>
            <p className="text-xs text-zinc-500 mt-0.5">Header / Body / Footer als Rich-Text · Spalten konfigurieren · Custom-Labels · WeasyPrint-PDF.</p>
          </div>
        </div>
        {items.length === 0 ? (
          <Empty icon={FileText} text="Lade Vorlagen…" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-5 py-3 text-left font-semibold">Name</th>
                <th className="px-3 py-3 text-left font-semibold">Typ</th>
                <th className="px-3 py-3 text-right font-semibold w-40">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`pdftpl-row-${it.id}`}>
                  <td className="px-5 py-3 text-zinc-100 font-medium">{it.name}</td>
                  <td className="px-3 py-3 text-zinc-400 text-xs">{it.label || it.doc_type}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <a href={`${BACKEND_URL}/api/vorlagen/pdf-templates/${it.id}/render-pdf`} target="_blank" rel="noopener noreferrer"
                        title="PDF-Vorschau"
                        className="p-1.5 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded inline-flex">
                        <Eye className="w-3.5 h-3.5" />
                      </a>
                      <button onClick={() => duplicate(it)} title="Duplizieren" data-testid={`pdftpl-dup-${it.id}`}
                        className="p-1.5 text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10 rounded">
                        <CopyIcon className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditing({ ...it })} data-testid={`pdftpl-edit-${it.id}`}
                        className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(it)} data-testid={`pdftpl-delete-${it.id}`}
                        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <PdfTemplateEditModal initial={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const COL_DEFS = [
  { key: "number",      defaultTitle: "Pos.",        align: "left"  },
  { key: "name",        defaultTitle: "Name",        align: "left"  },
  { key: "description", defaultTitle: "Bezeichnung", align: "left"  },
  { key: "unit",        defaultTitle: "Einheit",     align: "left"  },
  { key: "quantity",    defaultTitle: "Menge",       align: "right" },
  { key: "net",         defaultTitle: "Einzelpreis", align: "right" },
  { key: "tax",         defaultTitle: "MwSt",        align: "right" },
  { key: "gross",       defaultTitle: "Brutto",      align: "right" },
  { key: "total",       defaultTitle: "Gesamt",      align: "right" },
];

function PdfTemplateEditModal({ initial, meta, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [varSearch, setVarSearch] = useState("");
  const [activeRegion, setActiveRegion] = useState("body"); // header|body|footer|terms
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);
  const termsRef = useRef(null);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function getActiveRef() {
    return { header: headerRef, body: bodyRef, footer: footerRef, terms: termsRef }[activeRegion];
  }

  function insertVar(key) {
    const ref = getActiveRef();
    if (ref?.current?.insertText) {
      ref.current.insertText(key);
    } else {
      // fallback: append to body
      up("body_html", (form.body_html || "") + key);
    }
  }

  function updateColumn(idx, patch) {
    const cols = [...(form.columns || [])];
    cols[idx] = { ...cols[idx], ...patch };
    up("columns", cols);
  }

  async function preview() {
    try {
      const { data } = await api.post(`/vorlagen/pdf-templates/${initial.id}/render-html`, {});
      setPreviewHtml(data.html);
    } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
  }

  async function submit() {
    if (!form.name?.trim()) { toast.error("Name ist erforderlich"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.tenant_id; delete payload.label;
      delete payload.created_at; delete payload.updated_at;
      await api.put(`/vorlagen/pdf-templates/${initial.id}`, payload);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  }

  const filteredVars = (meta.placeholders || []).filter((p) =>
    !varSearch || p.label.toLowerCase().includes(varSearch.toLowerCase())
                || p.key.toLowerCase().includes(varSearch.toLowerCase())
                || p.category.toLowerCase().includes(varSearch.toLowerCase())
  );
  const groupedVars = filteredVars.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-[1500px] my-4 flex flex-col overflow-hidden max-h-[92vh]" onClick={(e) => e.stopPropagation()} data-testid="pdftpl-edit-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0 bg-zinc-900">
          <h3 className="font-display font-semibold text-zinc-100 inline-flex items-center gap-2">
            <FileText className="w-4 h-4 text-purple-400" />
            PDF-Vorlage: {initial.label || initial.doc_type}
          </h3>
          <div className="flex items-center gap-2">
            <a href={`${BACKEND_URL}/api/vorlagen/pdf-templates/${initial.id}/render-pdf`} target="_blank" rel="noopener noreferrer"
              data-testid="pdftpl-pdf-preview"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded">
              <Download className="w-3.5 h-3.5" /> PDF-Vorschau
            </a>
            <button onClick={preview} data-testid="pdftpl-html-preview"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">
              <Eye className="w-3.5 h-3.5" /> HTML-Vorschau
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px_240px] flex-1 min-h-0 overflow-hidden">
          {/* ===== Editor column ===== */}
          <div className="p-5 space-y-4 overflow-y-auto" onMouseDownCapture={(e) => {
            // track which editor region is active for variable insertion
            const t = e.target.closest("[data-region]");
            if (t) setActiveRegion(t.getAttribute("data-region"));
          }}>
            <div>
              <Label required>Vorlagenname</Label>
              <input value={form.name || ""} onChange={(e) => up("name", e.target.value)}
                data-testid="pdftpl-name" className={inputCls} />
            </div>

            <div data-region="header">
              <Label>Header (Briefkopf)</Label>
              <TiptapEditor ref={headerRef} value={form.header_html || ""}
                onChange={(html) => up("header_html", html)}
                placeholder="Logo links · Firma + Empfänger rechts" />
            </div>

            <div data-region="body">
              <Label>Anschreiben / Body</Label>
              <TiptapEditor ref={bodyRef} value={form.body_html || ""}
                onChange={(html) => up("body_html", html)}
                placeholder="Sehr geehrte Damen und Herren …" />
            </div>

            <div data-region="terms">
              <Label>Zahlungsbedingungen / Hinweis</Label>
              <TiptapEditor ref={termsRef} value={form.payment_terms_html || ""}
                onChange={(html) => up("payment_terms_html", html)}
                placeholder="Zahlungsziel: 14 Tage netto …" />
            </div>

            <div data-region="footer">
              <Label>Footer (Fußzeile)</Label>
              <TiptapEditor ref={footerRef} value={form.footer_html || ""}
                onChange={(html) => up("footer_html", html)}
                placeholder="Bankverbindung · UID · Kontakt" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Akzentfarbe</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent_color || "#2563EB"}
                    onChange={(e) => up("accent_color", e.target.value)}
                    className="w-10 h-9 rounded cursor-pointer" />
                  <input value={form.accent_color || ""} onChange={(e) => up("accent_color", e.target.value)}
                    className={inputCls + " font-mono uppercase text-xs"} />
                </div>
              </div>
              <div>
                <Label>Format</Label>
                <select value={form.paper_size || "A4"} onChange={(e) => up("paper_size", e.target.value)} className={inputCls}>
                  <option value="A4">A4</option>
                  <option value="Letter">Letter</option>
                </select>
              </div>
              <div>
                <Label>Rand (mm)</Label>
                <input type="number" min={5} max={50} value={form.margin_mm || 20}
                  onChange={(e) => up("margin_mm", parseInt(e.target.value) || 20)} className={inputCls} />
              </div>
            </div>

            {previewHtml && (
              <div className="border border-zinc-800 rounded-md bg-white" data-testid="pdftpl-preview-block">
                <div className="px-3 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-900 flex items-center justify-between">
                  <span>HTML-Vorschau</span>
                  <button onClick={() => setPreviewHtml(null)} className="text-zinc-400 hover:text-zinc-200"><X className="w-3 h-3" /></button>
                </div>
                <iframe title="preview" srcDoc={previewHtml}
                  className="w-full bg-white" style={{ height: 600, border: 0 }} />
              </div>
            )}
          </div>

          {/* ===== Column / Labels Sidebar ===== */}
          <div className="border-l border-zinc-800 p-4 overflow-y-auto bg-zinc-950" data-testid="pdftpl-columns">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 inline-flex items-center gap-1">
              <Palette className="w-3 h-3 text-purple-400" /> Spalten
            </div>
            <div className="space-y-1.5">
              {(form.columns || []).map((c, i) => {
                const def = COL_DEFS.find((d) => d.key === c.key);
                return (
                  <div key={c.key} className="bg-zinc-900 border border-zinc-800 rounded p-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] text-zinc-300">{def?.defaultTitle || c.key}</span>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={!!c.show}
                          onChange={(e) => updateColumn(i, { show: e.target.checked })}
                          data-testid={`pdftpl-col-show-${c.key}`}
                          className="w-3.5 h-3.5 accent-blue-600" />
                        <span className="text-[10px] text-zinc-500">Zeigen</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-[60px_1fr] gap-1">
                      <input type="number" min={0} max={100} value={c.width_pct || ""}
                        onChange={(e) => updateColumn(i, { width_pct: parseInt(e.target.value) || 0 })}
                        placeholder="%" disabled={!c.show}
                        className="bg-zinc-950 border border-zinc-800 rounded px-1.5 py-1 text-[11px] text-zinc-100 focus:outline-none focus:border-blue-500 disabled:opacity-40" />
                      <input value={c.title || ""} onChange={(e) => updateColumn(i, { title: e.target.value })}
                        placeholder={def?.defaultTitle || "Titel"} disabled={!c.show}
                        data-testid={`pdftpl-col-title-${c.key}`}
                        className="bg-zinc-950 border border-zinc-800 rounded px-1.5 py-1 text-[11px] text-zinc-100 focus:outline-none focus:border-blue-500 disabled:opacity-40" />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mt-4 mb-2">Summen-Labels</div>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-zinc-500">Netto-Label</label>
                <input value={form.net_label || ""} onChange={(e) => up("net_label", e.target.value)}
                  data-testid="pdftpl-net-label" className={inputCls + " text-xs"} placeholder="z.B. Zwischensumme" />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500">MwSt-Label</label>
                <input value={form.vat_label || ""} onChange={(e) => up("vat_label", e.target.value)}
                  className={inputCls + " text-xs"} />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500">Brutto-Label</label>
                <input value={form.gross_label || ""} onChange={(e) => up("gross_label", e.target.value)}
                  data-testid="pdftpl-gross-label" className={inputCls + " text-xs"} placeholder="z.B. Gesamtbetrag" />
              </div>
            </div>
          </div>

          {/* ===== Variable Sidebar ===== */}
          <div className="border-l border-zinc-800 bg-zinc-950 p-3 overflow-y-auto" data-testid="pdftpl-var-sidebar">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1 mb-2">
              <Sparkles className="w-3 h-3 text-blue-400" /> Variablen
            </div>
            <div className="text-[9px] text-zinc-600 mb-2">Aktiver Bereich: <span className="text-zinc-300 capitalize">{activeRegion}</span></div>
            <div className="relative mb-2">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={varSearch} onChange={(e) => setVarSearch(e.target.value)}
                placeholder="Suchen…" className="w-full bg-zinc-900 border border-zinc-800 rounded pl-6 pr-2 py-1 text-[11px] text-zinc-100 focus:outline-none focus:border-blue-500" />
            </div>
            <div className="space-y-2">
              {Object.entries(groupedVars).map(([cat, vars]) => (
                <div key={cat}>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{cat}</div>
                  <div className="flex flex-col gap-0.5">
                    {vars.map((v) => (
                      <button key={v.key} onClick={() => insertVar(v.key)} type="button"
                        className="text-left text-[10px] px-2 py-1 bg-zinc-900 hover:bg-blue-600 hover:text-white text-zinc-300 rounded font-mono transition-colors truncate"
                        title={v.label}>
                        {v.key}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 flex-shrink-0 bg-zinc-900">
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid="pdftpl-save"
            className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded">
            <Save className="w-3.5 h-3.5" /> {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// 4) Global Settings
// ===========================================================================
function GlobalSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const { data } = await api.get("/vorlagen/settings"); setCfg(data); setDraft(data); }
    catch { toast.error("Fehler"); }
  }
  useEffect(() => { load(); }, []);
  if (!draft) return <div className="text-zinc-500 text-sm">Lade…</div>;

  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);

  async function save() {
    setSaving(true);
    try {
      const payload = { ...draft };
      delete payload.key; delete payload.tenant_id; delete payload.updated_at;
      await api.put("/vorlagen/settings", payload);
      toast.success("Gespeichert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 space-y-4 max-w-2xl">
      <div>
        <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
          <SettingsIcon className="w-4 h-4 text-blue-400" /> Globale Vorlagen-Einstellungen
        </h4>
        <p className="text-xs text-zinc-500 mt-0.5">Werden bei E-Mail-Versand als Default verwendet, sofern in der Vorlage nicht überschrieben.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Absender-Name (Default)</Label>
          <input value={draft.default_from_name || ""}
            onChange={(e) => setDraft({ ...draft, default_from_name: e.target.value })}
            data-testid="vorlagen-from-name" className={inputCls} placeholder="z.B. Munja Trans GmbH" />
        </div>
        <div>
          <Label>Absender-E-Mail (Default)</Label>
          <input type="email" value={draft.default_from_email || ""}
            onChange={(e) => setDraft({ ...draft, default_from_email: e.target.value })}
            className={inputCls} placeholder="z.B. office@munjatrans.at" />
        </div>
      </div>
      <div>
        <Label>Reply-To-E-Mail</Label>
        <input type="email" value={draft.default_reply_to || ""}
          onChange={(e) => setDraft({ ...draft, default_reply_to: e.target.value })}
          className={inputCls} placeholder="(optional)" />
      </div>
      <div>
        <Label>Standard-Signatur (HTML/Text)</Label>
        <textarea rows={4} value={draft.default_signature || ""}
          onChange={(e) => setDraft({ ...draft, default_signature: e.target.value })}
          data-testid="vorlagen-signature" className={inputCls + " font-mono text-xs"}
          placeholder="Mit freundlichen Grüßen,&#10;Munja Trans GmbH&#10;Tel: …&#10;www.munjatrans.at" />
      </div>
      <div>
        <Label>Standard-E-Mail-Footer (HTML)</Label>
        <textarea rows={4} value={draft.default_footer_html || ""}
          onChange={(e) => setDraft({ ...draft, default_footer_html: e.target.value })}
          className={inputCls + " font-mono text-xs"}
          placeholder="<p style='font-size:10px;color:#999'>Diese E-Mail enthält vertrauliche Informationen…</p>" />
      </div>

      <div className="flex justify-end pt-2">
        <button onClick={save} disabled={!dirty || saving} data-testid="vorlagen-settings-save"
          className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded">
          <Save className="w-3.5 h-3.5" /> {saving ? "Speichere…" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// 5) Contracts
// ===========================================================================
const CONTRACT_TYPES = [
  { key: "arbeitsvertrag",  label: "Arbeitsvertrag" },
  { key: "dienstleistung",  label: "Dienstleistungsvertrag" },
  { key: "nda",             label: "NDA / Geheimhaltung" },
  { key: "mietvertrag",     label: "Mietvertrag" },
  { key: "sonstiges",       label: "Sonstiges" },
];

function ContractsManager({ meta }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  async function load() {
    try { const { data } = await api.get("/vorlagen/contracts"); setItems(data || []); }
    catch { toast.error("Fehler"); }
  }
  useEffect(() => { load(); }, []);

  async function remove(it) {
    if (!window.confirm(`Vertrag „${it.name}" löschen?`)) return;
    try { await api.delete(`/vorlagen/contracts/${it.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
              <Scroll className="w-4 h-4 text-amber-400" /> Vertrags-Vorlagen
            </h4>
            <p className="text-xs text-zinc-500 mt-0.5">Benutzerdefinierte Verträge mit Header / Body / Footer · Variablen-Substitution · WeasyPrint-PDF.</p>
          </div>
          <button onClick={() => setEditing({
              __new: true, contract_type: "arbeitsvertrag", entity_type: "employee",
              is_active: true, name: "", description: "",
              header_html: "", body_html: "", footer_html: "",
            })}
            data-testid="contract-add-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
            <Plus className="w-3.5 h-3.5" /> Neuer Vertrag
          </button>
        </div>
        {items.length === 0 ? (
          <Empty icon={Scroll} text="Noch keine Vertrags-Vorlagen." />
        ) : (
          <div className="divide-y divide-zinc-800">
            {items.map((it) => {
              const t = CONTRACT_TYPES.find((c) => c.key === it.contract_type);
              const ent = (meta.contract_entity_types || []).find((x) => x.key === it.entity_type);
              return (
                <div key={it.id} data-testid={`contract-row-${it.id}`}
                  className="px-5 py-3 hover:bg-zinc-900/40 flex items-center gap-3">
                  <Scroll className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-100 font-medium text-sm">{it.name}</span>
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                        {t?.label || it.contract_type}
                      </span>
                      {ent && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                          {ent.label}
                        </span>
                      )}
                      {!it.is_active && <span className="text-[9px] uppercase text-zinc-500">Inaktiv</span>}
                    </div>
                    {it.description && <div className="text-xs text-zinc-400 mt-0.5">{it.description}</div>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <a href={`${BACKEND_URL}/api/vorlagen/contracts/${it.id}/render-pdf`} target="_blank" rel="noopener noreferrer"
                      title="PDF-Vorschau"
                      className="p-1.5 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded inline-flex">
                      <Eye className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={() => setEditing({ ...it })} data-testid={`contract-edit-${it.id}`}
                      className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(it)} data-testid={`contract-delete-${it.id}`}
                      className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && <ContractEditModal initial={editing} meta={meta} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ContractEditModal({ initial, meta, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [varSearch, setVarSearch] = useState("");
  const [activeRegion, setActiveRegion] = useState("body");
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function getRef() {
    return { header: headerRef, body: bodyRef, footer: footerRef }[activeRegion];
  }
  function insertVar(key) {
    const ref = getRef();
    if (ref?.current?.insertText) ref.current.insertText(key);
    else up("body_html", (form.body_html || "") + key);
  }

  async function submit() {
    if (!form.name?.trim()) { toast.error("Name ist erforderlich"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.__new; delete payload.id; delete payload.tenant_id;
      delete payload.created_at; delete payload.updated_at;
      if (isNew) await api.post("/vorlagen/contracts", payload);
      else await api.put(`/vorlagen/contracts/${initial.id}`, payload);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  }

  // Filter placeholders by selected entity_type using meta.contract_entity_categories
  const allowedCats = useMemo(() => {
    const map = meta.contract_entity_categories || {};
    return new Set(map[form.entity_type] || []);
  }, [meta, form.entity_type]);

  const filteredVars = (meta.placeholders || [])
    .filter((p) => allowedCats.size === 0 || allowedCats.has(p.category))
    .filter((p) => !varSearch
                || p.label.toLowerCase().includes(varSearch.toLowerCase())
                || p.key.toLowerCase().includes(varSearch.toLowerCase())
                || p.category.toLowerCase().includes(varSearch.toLowerCase()));
  const groupedVars = filteredVars.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-[1400px] my-4 flex flex-col overflow-hidden max-h-[92vh]" onClick={(e) => e.stopPropagation()} data-testid="contract-edit-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0 bg-zinc-900">
          <h3 className="font-display font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Scroll className="w-4 h-4 text-amber-400" />
            {isNew ? "Neuer Vertrag" : `Vertrag bearbeiten: ${initial.name}`}
          </h3>
          <div className="flex items-center gap-2">
            {!isNew && (
              <a href={`${BACKEND_URL}/api/vorlagen/contracts/${initial.id}/render-pdf`} target="_blank" rel="noopener noreferrer"
                data-testid="contract-pdf-preview"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded">
                <Download className="w-3.5 h-3.5" /> PDF-Vorschau
              </a>
            )}
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] flex-1 min-h-0 overflow-hidden">
          {/* ===== Editor column ===== */}
          <div className="p-5 space-y-4 overflow-y-auto" onMouseDownCapture={(e) => {
            const t = e.target.closest("[data-region]");
            if (t) setActiveRegion(t.getAttribute("data-region"));
          }}>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label required>Name</Label>
                <input value={form.name || ""} onChange={(e) => up("name", e.target.value)}
                  data-testid="contract-modal-name" className={inputCls}
                  placeholder="z.B. Arbeitsvertrag Fahrer Standard 2026" />
              </div>
              <div>
                <Label required>Abteilung zur Verwendung</Label>
                <select value={form.entity_type || "employee"} onChange={(e) => up("entity_type", e.target.value)}
                  data-testid="contract-modal-entity" className={inputCls}>
                  {(meta.contract_entity_types || []).map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Beschreibung</Label>
                <input value={form.description || ""} onChange={(e) => up("description", e.target.value)} className={inputCls} />
              </div>
              <div>
                <Label>Vertrags-Typ</Label>
                <select value={form.contract_type} onChange={(e) => up("contract_type", e.target.value)} className={inputCls}>
                  {CONTRACT_TYPES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <div data-region="header">
              <Label>Vertragskopf</Label>
              <TiptapEditor ref={headerRef} value={form.header_html || ""}
                onChange={(html) => up("header_html", html)}
                placeholder="Briefkopf · Empfänger · Datum" />
            </div>

            <div data-region="body">
              <Label required>Vertragsinhalt</Label>
              <TiptapEditor ref={bodyRef} value={form.body_html || ""}
                onChange={(html) => up("body_html", html)}
                placeholder="§1 Vertragsgegenstand · §2 Leistungen · …" />
            </div>

            <div data-region="footer">
              <Label>Vertragsfußzeile</Label>
              <TiptapEditor ref={footerRef} value={form.footer_html || ""}
                onChange={(html) => up("footer_html", html)}
                placeholder="Unterschriften · Datum · Ort" />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => up("is_active", e.target.checked)}
                className="w-4 h-4 rounded accent-blue-600" />
              <span className="text-sm text-zinc-200">Aktiv</span>
            </label>
          </div>

          {/* ===== Context-aware variable sidebar ===== */}
          <div className="border-l border-zinc-800 bg-zinc-950 p-3 overflow-y-auto" data-testid="contract-var-sidebar">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1 mb-1">
              <Sparkles className="w-3 h-3 text-amber-400" /> Tags ({form.entity_type || "?"})
            </div>
            <div className="text-[9px] text-zinc-600 mb-2">Aktiver Bereich: <span className="text-zinc-300 capitalize">{activeRegion}</span></div>
            <div className="relative mb-2">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={varSearch} onChange={(e) => setVarSearch(e.target.value)}
                placeholder="Suchen…"
                data-testid="contract-var-search"
                className="w-full bg-zinc-900 border border-zinc-800 rounded pl-6 pr-2 py-1 text-[11px] text-zinc-100 focus:outline-none focus:border-amber-500" />
            </div>
            <div className="space-y-2.5">
              {Object.entries(groupedVars).map(([cat, vars]) => (
                <div key={cat}>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{cat}</div>
                  <div className="flex flex-col gap-0.5">
                    {vars.map((v) => (
                      <button key={v.key} onClick={() => insertVar(v.key)} type="button"
                        data-testid={`contract-var-${v.key.replaceAll('#','')}`}
                        className="text-left text-[10px] px-2 py-1 bg-zinc-900 hover:bg-amber-600 hover:text-white text-zinc-300 rounded font-mono transition-colors flex items-center justify-between gap-2 group"
                        title={v.label}>
                        <span className="truncate">{v.key}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {Object.keys(groupedVars).length === 0 && (
                <p className="text-[10px] text-zinc-600 text-center py-4">Keine passenden Variablen.</p>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 flex-shrink-0 bg-zinc-900">
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid="contract-modal-save"
            className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded">
            <Save className="w-3.5 h-3.5" /> {saving ? "Speichere…" : isNew ? "Anlegen" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// 6) Voreingestellte Dokumentenanhänge (Event → Document Categories)
// ===========================================================================
function DocPresetsManager({ meta }) {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/vorlagen/doc-presets");
      setItems(data || []);
      const d = {};
      (data || []).forEach((it) => { d[it.event_key] = [...(it.doc_categories || [])]; });
      setDrafts(d);
    } catch { toast.error("Konnte Voreinstellungen nicht laden"); }
  }
  useEffect(() => { load(); }, []);

  function toggle(eventKey, cat) {
    setDrafts((d) => {
      const set = new Set(d[eventKey] || []);
      if (set.has(cat)) set.delete(cat); else set.add(cat);
      return { ...d, [eventKey]: Array.from(set) };
    });
  }

  function selectAll(eventKey, cats) {
    setDrafts((d) => ({ ...d, [eventKey]: [...cats] }));
  }
  function clearAll(eventKey) {
    setDrafts((d) => ({ ...d, [eventKey]: [] }));
  }

  async function save(eventKey) {
    setSavingKey(eventKey);
    try {
      await api.put(`/vorlagen/doc-presets/${eventKey}`, { doc_categories: drafts[eventKey] || [] });
      toast.success("Gespeichert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSavingKey(null); }
  }

  function isDirty(it) {
    const orig = (it.doc_categories || []).slice().sort().join("|");
    const cur = (drafts[it.event_key] || []).slice().sort().join("|");
    return orig !== cur;
  }

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <Files className="w-4 h-4 text-cyan-400" /> Voreingestellte Dokumentenanhänge
          </h4>
          <p className="text-xs text-zinc-500 mt-0.5">
            Pro Anlass legen Sie fest, welche Mitarbeiter-/Fahrzeug-Dokumente automatisch
            als Anhang vorgeschlagen werden — beim Versand sichtbar im Bestätigungs-Dialog.
          </p>
        </div>
        <div className="divide-y divide-zinc-800">
          {items.length === 0 ? (
            <Empty icon={Files} text="Lade Anlässe…" />
          ) : (
            items.map((it) => {
              const cats = (meta.doc_categories || {})[it.owner] || [];
              const selected = drafts[it.event_key] || [];
              const dirty = isDirty(it);
              return (
                <div key={it.event_key} className="p-5" data-testid={`docpreset-row-${it.event_key}`}>
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                      <div className="text-zinc-100 font-medium text-sm flex items-center gap-2">
                        {it.label || it.event_key}
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                          {it.owner === "vehicle" ? "Fahrzeug" : "Mitarbeiter"}
                        </span>
                        {selected.length > 0 && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                            {selected.length} aktiv
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        Auswahl wird beim Senden mit den real verfügbaren Dokumenten der Person/des Fahrzeugs verknüpft.
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button type="button" onClick={() => selectAll(it.event_key, cats)}
                        data-testid={`docpreset-all-${it.event_key}`}
                        className="text-[10px] px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded">
                        Alle
                      </button>
                      <button type="button" onClick={() => clearAll(it.event_key)}
                        data-testid={`docpreset-none-${it.event_key}`}
                        className="text-[10px] px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded">
                        Keine
                      </button>
                      <button type="button" onClick={() => save(it.event_key)}
                        disabled={!dirty || savingKey === it.event_key}
                        data-testid={`docpreset-save-${it.event_key}`}
                        className={`inline-flex items-center gap-1 text-[11px] px-3 py-1 rounded font-medium ${dirty ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-zinc-800 text-zinc-500"} disabled:opacity-50`}>
                        <Save className="w-3 h-3" /> {savingKey === it.event_key ? "Speichere…" : "Speichern"}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cats.map((c) => {
                      const active = selected.includes(c);
                      return (
                        <button key={c} type="button" onClick={() => toggle(it.event_key, c)}
                          data-testid={`docpreset-cat-${it.event_key}-${c}`}
                          className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border transition-colors ${active
                            ? "bg-cyan-500/15 text-cyan-200 border-cyan-500/40"
                            : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"}`}>
                          {active && <Check className="w-3 h-3" />} {c}
                        </button>
                      );
                    })}
                    {cats.length === 0 && (
                      <p className="text-[11px] text-zinc-600">Keine Dokumentarten in dieser Kategorie.</p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}


// ===========================================================================
// 7) Event → Template Mappings (Phase 5L — Vorlagen-Zuordnungen)
// ===========================================================================
function EventMappingsTab() {
  const [rows, setRows] = useState([]);
  const [emails, setEmails] = useState([]);
  const [smses, setSmses] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [filter, setFilter] = useState("");

  async function load() {
    try {
      const [m, t, p] = await Promise.all([
        api.get("/vorlagen/event-mappings"),
        api.get("/vorlagen/templates"),
        api.get("/vorlagen/pdf-templates"),
      ]);
      setRows(m.data || []);
      setEmails((t.data || []).filter((x) => x.category === "email"));
      setSmses((t.data || []).filter((x) => x.category === "sms"));
      setPdfs(p.data || []);
      const d = {};
      (m.data || []).forEach((r) => {
        d[r.event_key] = {
          email_template_id: r.email_template_id || "",
          sms_template_id: r.sms_template_id || "",
          pdf_template_id: r.pdf_template_id || "",
        };
      });
      setDrafts(d);
    } catch { toast.error("Konnte Zuordnungen nicht laden"); }
  }
  useEffect(() => { load(); }, []);

  function update(eventKey, field, value) {
    setDrafts((d) => ({ ...d, [eventKey]: { ...d[eventKey], [field]: value } }));
  }

  async function save(eventKey) {
    setSavingKey(eventKey);
    try {
      const payload = {
        email_template_id: drafts[eventKey]?.email_template_id || null,
        sms_template_id: drafts[eventKey]?.sms_template_id || null,
        pdf_template_id: drafts[eventKey]?.pdf_template_id || null,
      };
      await api.put(`/vorlagen/event-mappings/${eventKey}`, payload);
      toast.success("Gespeichert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSavingKey(null); }
  }

  function isDirty(r) {
    const cur = drafts[r.event_key] || {};
    return (cur.email_template_id || "") !== (r.email_template_id || "")
        || (cur.sms_template_id   || "") !== (r.sms_template_id   || "")
        || (cur.pdf_template_id   || "") !== (r.pdf_template_id   || "");
  }

  const filtered = rows.filter((r) =>
    !filter || r.label.toLowerCase().includes(filter.toLowerCase())
            || r.event_key.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid="event-mappings-tab">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h4 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-cyan-400" /> Vorlagen-Zuordnungen
          </h4>
          <p className="text-xs text-zinc-500 mt-0.5">
            Pro System-Event festlegen, welche E-Mail-, SMS- und PDF-Vorlage automatisch verwendet wird.
          </p>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Event suchen…" data-testid="mappings-search"
            className="bg-zinc-950 border border-zinc-800 rounded pl-7 pr-3 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 w-48" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-5 py-3 text-left font-semibold w-72">Event</th>
              <th className="px-3 py-3 text-left font-semibold">E-Mail-Vorlage</th>
              <th className="px-3 py-3 text-left font-semibold">SMS-Vorlage</th>
              <th className="px-3 py-3 text-left font-semibold">PDF-Vorlage</th>
              <th className="px-3 py-3 text-right font-semibold w-28"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const draft = drafts[r.event_key] || {};
              const dirty = isDirty(r);
              const pdfChoices = r.applicable_pdf_doc_type
                ? pdfs.filter((p) => p.doc_type === r.applicable_pdf_doc_type)
                : [];
              return (
                <tr key={r.event_key} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                  data-testid={`mapping-row-${r.event_key}`}>
                  <td className="px-5 py-2.5">
                    <div className="text-zinc-100 text-sm">{r.label}</div>
                    <div className="text-[10px] text-zinc-600 font-mono">{r.event_key}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={draft.email_template_id || ""} onChange={(e) => update(r.event_key, "email_template_id", e.target.value)}
                      data-testid={`mapping-email-${r.event_key}`}
                      className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 w-full">
                      <option value="">— Bitte auswählen —</option>
                      {emails.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={draft.sms_template_id || ""} onChange={(e) => update(r.event_key, "sms_template_id", e.target.value)}
                      data-testid={`mapping-sms-${r.event_key}`}
                      className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 w-full">
                      <option value="">— Bitte auswählen —</option>
                      {smses.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    {r.applicable_pdf_doc_type ? (
                      <select value={draft.pdf_template_id || ""} onChange={(e) => update(r.event_key, "pdf_template_id", e.target.value)}
                        data-testid={`mapping-pdf-${r.event_key}`}
                        className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-100 focus:outline-none focus:border-blue-500 w-full">
                        <option value="">— Bitte auswählen —</option>
                        {pdfChoices.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    ) : (
                      <span className="text-[10px] text-zinc-600 italic">nicht anwendbar</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button type="button" onClick={() => save(r.event_key)}
                      disabled={!dirty || savingKey === r.event_key}
                      data-testid={`mapping-save-${r.event_key}`}
                      className={`inline-flex items-center gap-1 text-[11px] px-3 py-1 rounded font-medium ${dirty ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-zinc-800 text-zinc-500"} disabled:opacity-50`}>
                      <Save className="w-3 h-3" /> {savingKey === r.event_key ? "…" : "Speichern"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-500 text-sm">Keine Events gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ===========================================================================
// Helpers
// ===========================================================================
function Label({ children, required }) {
  return <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{children} {required && <span className="text-red-400">*</span>}</label>;
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="text-center py-12">
      <Icon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
      <p className="text-zinc-500 text-sm">{text}</p>
    </div>
  );
}
